import React from 'react';
import { UncontrolledTooltip } from 'reactstrap';
import DragVerticalIcon from 'mdi-react/DragVerticalIcon';
import ChevronUpIcon from 'mdi-react/ChevronUpIcon';
import ChevronDownIcon from 'mdi-react/ChevronDownIcon';
import EditOutlineIcon from 'mdi-react/EditOutlineIcon';
import DeleteOutlineIcon from 'mdi-react/DeleteOutlineIcon';
import SquareIconButton from '../../../../shared/components/custom/SquareIconButton';
import { getQuestionType } from './QuestionType';
import { partial } from '../../../../util/translation/translation';

const ASK_FOR_OPTIONS = ['participant', 'guests', 'both'];

// One question in the event's form: its text, type and options, plus the
// per-question settings (required, who is asked) and actions in a footer.
export default function PickedQuestionItem(props) {
  const { question, onUpdate, index, first, last, editability } = props;
  const qT = partial('question');
  const type = getQuestionType(question.question_type);
  const TypeIcon = type?.icon;

  const isRequired =
    Array.isArray(question.required) && question.required.includes('required');

  const toggleRequired = () => {
    if (!onUpdate) return;
    const next = { ...question, required: ['required'] };
    if (isRequired) delete next.required;
    onUpdate(next);
  };

  // Who the question is asked of; a missing value means the participant.
  // Reservations are booked once for the whole group, so never per guest.
  const isReservation = question.question_type === 'reservation';
  const askFor = isReservation
    ? 'participant'
    : question.askFor || 'participant';

  const setAskFor = (value) => {
    if (!onUpdate || value === askFor) return;
    const next = { ...question, askFor: value };
    if (value === 'participant') delete next.askFor;
    onUpdate(next);
  };

  const askForLabel = {
    participant: qT('askForParticipant'),
    guests: qT('askForGuests'),
    both: qT('askForBoth'),
  };

  // A question added since the last save can't be edited until it's saved
  const editLocked = !!editability.find((e) => e === question.id);

  return (
    <div className="qb-question">
      <div className="qb-question__main">
        <div className="qb-question__rail">
          <span className="qb-question__num">{index + 1}</span>
          <span className="qb-question__grip" aria-hidden>
            <DragVerticalIcon size={18} />
          </span>
        </div>
        <div className="qb-question__body">
          <div className="qb-question__head">
            <h3 className="qb-question__text">
              {question.question}
              {isRequired && (
                <span className="qb-question__required" aria-hidden>
                  {' *'}
                </span>
              )}
            </h3>
            {type && (
              <span className="qb-chip">
                {TypeIcon && <TypeIcon />}
                {type.label}
              </span>
            )}
          </div>
          {Array.isArray(question.choices) && question.choices.length > 0 && (
            <div className="qb-question__options">
              {question.choices.map((opt) => (
                <span key={opt.choice} className="qb-option">
                  {opt.choice}
                </span>
              ))}
            </div>
          )}
          {Array.isArray(question.options) && question.options.length > 0 && (
            <div className="qb-question__options">
              {question.options.map((opt) => {
                const booked = opt.booked ?? 0;
                const full = opt.capacity && booked >= opt.capacity;
                return (
                  <span
                    key={opt.id}
                    className={`qb-option${full ? ' qb-option--full' : ''}`}
                  >
                    {opt.title}
                    {opt.subtitle && (
                      <span className="qb-option__meta">{opt.subtitle}</span>
                    )}
                    <span className="qb-option__meta">
                      {`${booked}/${opt.capacity}`}
                    </span>
                  </span>
                );
              })}
            </div>
          )}
        </div>
      </div>
      <div className="qb-question__foot">
        <label className="qb-toggle">
          <button
            type="button"
            role="switch"
            aria-checked={isRequired}
            className={`qb-switch${isRequired ? ' qb-switch--on' : ''}`}
            onClick={toggleRequired}
          >
            <span className="qb-switch__knob" />
          </button>
          {qT('required')}
        </label>
        {/* Reservations are always booked once for the whole group, so
            there's no choice of who is asked - just say so. */}
        {isReservation ? (
          <small className="qb-question__note">
            {qT('reservationParticipantOnly')}
          </small>
        ) : (
          <div className="qb-ask">
            <span>{qT('askFor')}</span>
            <div
              className="qb-segment"
              role="radiogroup"
              aria-label={qT('askFor')}
            >
              {ASK_FOR_OPTIONS.map((value) => (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={askFor === value}
                  className={`qb-segment__btn${
                    askFor === value ? ' qb-segment__btn--on' : ''
                  }`}
                  onClick={() => setAskFor(value)}
                >
                  {askForLabel[value]}
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="qb-question__actions">
          <SquareIconButton
            icon={<ChevronUpIcon />}
            title={qT('moveUp')}
            id={`move-up-${question.id}`}
            disabled={first}
            onClick={() => props.onMoveUp(question)}
          />
          <SquareIconButton
            icon={<ChevronDownIcon />}
            title={qT('moveDown')}
            id={`move-down-${question.id}`}
            disabled={last}
            onClick={() => props.onMoveDown(question)}
          />
          {/* Wrapped so the "save first" tooltip still shows while the
              button is disabled (disabled buttons get no hover events). */}
          <span id={`edit-wrap-${question.id}`}>
            <SquareIconButton
              icon={<EditOutlineIcon />}
              title={editLocked ? '' : qT('editQuestion')}
              id={`edit-${question.id}`}
              disabled={editLocked}
              onClick={() => props.onEdit(question)}
            />
          </span>
          {editLocked && (
            <UncontrolledTooltip
              placement="top"
              target={`edit-wrap-${question.id}`}
            >
              {qT('pleaseSaveTheQuestionnaireToEditThisQuestion')}
            </UncontrolledTooltip>
          )}
          <SquareIconButton
            icon={<DeleteOutlineIcon />}
            title={qT('removeFromForm')}
            tone="danger"
            id={`remove-${question.id}`}
            onClick={() => props.onDelete(question)}
          />
        </div>
      </div>
    </div>
  );
}
